<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ScenariosAPI from 'dashboard/api/scenarios';
import RunDetails from './RunDetails.vue';
import en from './en.json';
import ru from './ru.json';
import { startMessages } from './startSchedule';

const props = defineProps({
  scenario: { type: Object, required: true },
  catalog: { type: Object, default: null },
});
const emit = defineEmits(['close']);
const { t, locale } = useI18n({
  useScope: 'local',
  messages: {
    en: { ...en, ...startMessages.en },
    ru: { ...ru, ...startMessages.ru },
  },
});
const heading = ref(null);
const detailPanel = ref(null);
const query = ref('');
const status = ref('');
const from = ref('');
const until = ref('');
const rows = ref([]);
const counts = ref({});
const cursor = ref(null);
const observedAt = ref(null);
const loading = ref(false);
const detailLoading = ref(false);
const error = ref('');
const detailError = ref('');
const selectedId = ref(null);
const detail = ref(null);
let listRequest = 0;
let detailRequest = 0;
let applied = {};
const statuses = [
  'active',
  'waiting',
  'completed',
  'exited',
  'failed',
  'blocked',
];
const dateLabel = value =>
  value
    ? new Intl.DateTimeFormat(locale.value, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : '—';
const statusLabel = value =>
  statuses.includes(value) ? t(`runs.status.${value}`) : t('runs.unknown');

const select = async (id, focus = true) => {
  selectedId.value = id;
  detail.value = null;
  detailError.value = '';
  detailLoading.value = true;
  detailRequest += 1;
  const request = detailRequest;
  try {
    const response = await ScenariosAPI.run(props.scenario.id, id);
    if (request === detailRequest) detail.value = response.data;
  } catch {
    if (request === detailRequest) detailError.value = t('runs.detailFailed');
  } finally {
    if (request === detailRequest) {
      detailLoading.value = false;
      await nextTick();
      if (focus && request === detailRequest && selectedId.value === id) {
        detailPanel.value?.focus();
      }
    }
  }
};

const load = async (more = false, refresh = false) => {
  listRequest += 1;
  const request = listRequest;
  loading.value = true;
  error.value = '';
  if (!more && !refresh) {
    selectedId.value = null;
    detail.value = null;
    detailRequest += 1;
    detailLoading.value = false;
    rows.value = [];
    counts.value = {};
    cursor.value = null;
    observedAt.value = null;
  }
  try {
    if (!more && !refresh) {
      if (from.value && until.value && from.value > until.value) {
        throw new Error('period');
      }
      const end = until.value ? new Date(`${until.value}T00:00:00`) : null;
      if (end) end.setDate(end.getDate() + 1);
      applied = {
        query: query.value.trim(),
        status: status.value,
        from_at: from.value
          ? new Date(`${from.value}T00:00:00`).toISOString()
          : undefined,
        until_at: end?.toISOString(),
      };
    }
    const response = await ScenariosAPI.runs(props.scenario.id, {
      ...applied,
      before: more ? cursor.value : undefined,
    });
    if (request !== listRequest) return;
    rows.value = more
      ? [...rows.value, ...response.data.runs]
      : response.data.runs;
    counts.value = response.data.counts;
    cursor.value = response.data.next_cursor;
    observedAt.value = response.data.observed_at;
    if (refresh && selectedId.value) select(selectedId.value, false);
  } catch (failure) {
    if (request !== listRequest) return;
    error.value = t(
      failure.message === 'period' ? 'runs.invalidPeriod' : 'runs.loadFailed'
    );
  } finally {
    if (request === listRequest) loading.value = false;
  }
};
onMounted(async () => {
  await nextTick();
  heading.value?.focus();
  load();
});
onBeforeUnmount(() => {
  listRequest += 1;
  detailRequest += 1;
});
</script>

<template>
  <section
    class="flex flex-col h-full min-h-0 min-w-0 bg-n-background"
    :aria-label="t('runs.title')"
  >
    <header
      class="shrink-0 flex flex-wrap items-center gap-3 px-5 py-3 border-b border-n-weak bg-n-solid-1"
    >
      <Button
        :label="t('runs.back')"
        icon="i-lucide-arrow-left"
        variant="ghost"
        @click="emit('close')"
      />
      <div class="min-w-0 flex-1">
        <h2 ref="heading" tabindex="-1" class="m-0 text-base font-semibold">
          {{ t('runs.title') }}
        </h2>
        <p class="m-0 text-xs text-n-slate-11 truncate">{{ scenario.name }}</p>
      </div>
      <Button
        :label="t('runs.refresh')"
        icon="i-lucide-refresh-cw"
        variant="ghost"
        :disabled="loading"
        @click="load(false, true)"
      />
    </header>
    <form
      class="shrink-0 flex flex-wrap items-end gap-3 px-5 py-3 border-b border-n-weak"
      @submit.prevent="load()"
    >
      <label class="flex-1 min-w-48 text-xs text-n-slate-11">
        {{ t('runs.search') }}
        <input
          v-model="query"
          type="search"
          maxlength="120"
          :placeholder="t('runs.searchHint')"
          class="!mb-0 mt-1 !text-sm w-full"
        />
      </label>
      <label class="text-xs text-n-slate-11">
        {{ t('runs.statusLabel') }}
        <select v-model="status" class="!mb-0 mt-1 !text-sm">
          <option value="">
            {{ t('runs.all') }} ·
            {{ Object.values(counts).reduce((sum, count) => sum + count, 0) }}
          </option>
          <option v-for="item in statuses" :key="item" :value="item">
            {{ statusLabel(item) }} · {{ counts[item] || 0 }}
          </option>
        </select>
      </label>
      <label class="text-xs text-n-slate-11">
        {{ t('runs.from') }}
        <input
          v-model="from"
          type="date"
          :aria-label="t('runs.from')"
          class="!mb-0 mt-1 !text-sm"
        />
      </label>
      <label class="text-xs text-n-slate-11">
        {{ t('runs.until') }}
        <input
          v-model="until"
          type="date"
          :aria-label="t('runs.until')"
          class="!mb-0 mt-1 !text-sm"
        />
      </label>
      <Button type="submit" :label="t('runs.find')" :disabled="loading" />
    </form>
    <p
      class="shrink-0 px-5 py-2 m-0 text-xs text-n-slate-11 border-b border-n-weak"
    >
      {{ t('runs.scope') }}
    </p>
    <p v-if="error" class="px-5 py-2 text-sm text-n-ruby-11" role="alert">
      {{ error }}
    </p>
    <div class="flex flex-1 min-h-0 min-w-0">
      <div
        class="w-full md:w-80 lg:w-96 md:shrink-0 overflow-y-auto md:border-e border-n-weak"
        :class="{ 'max-md:hidden': selectedId }"
        :aria-busy="loading"
      >
        <p v-if="loading && !rows.length" class="p-5 text-sm" role="status">
          {{ t('loading') }}
        </p>
        <div v-else-if="!rows.length && !error" class="p-5">
          <p class="text-sm font-medium">{{ t('runs.empty') }}</p>
          <p class="text-xs text-n-slate-11">{{ t('runs.emptyHint') }}</p>
        </div>
        <ul class="list-none p-0 m-0 divide-y divide-n-weak">
          <li v-for="row in rows" :key="row.id">
            <button
              type="button"
              class="w-full text-start p-4 transition-colors hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="selectedId === row.id ? 'bg-n-alpha-2' : ''"
              :aria-pressed="selectedId === row.id"
              @click="select(row.id)"
            >
              <span class="block font-medium text-sm break-words">{{
                row.contact.name || t('runs.contact', { id: row.contact.id })
              }}</span>
              <span class="block text-xs text-n-slate-11 mt-1">{{
                row.contact.phone || '—'
              }}</span>
              <span class="flex flex-wrap gap-x-2 mt-2 text-xs"
                ><span
                  :class="
                    ['failed', 'blocked'].includes(row.status)
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-12'
                  "
                  >{{ statusLabel(row.status) }}</span
                ><span class="text-n-slate-11">{{
                  dateLabel(row.created_at)
                }}</span></span
              >
              <span class="block mt-1 text-xs text-n-slate-11"
                >{{ t('runs.run', { id: row.id }) }} ·
                {{
                  t(
                    row.event.patient_id
                      ? 'startSchedule.patientRecord'
                      : 'runs.appointment',
                    { id: row.event.patient_id || row.event.appointment_id }
                  )
                }}</span
              >
            </button>
          </li>
        </ul>
        <div v-if="cursor" class="p-4">
          <Button
            :label="t('runs.more')"
            variant="ghost"
            :disabled="loading"
            @click="load(true)"
          />
        </div>
      </div>
      <section
        ref="detailPanel"
        tabindex="-1"
        class="flex-1 min-w-0 overflow-y-auto p-5"
        :class="{ 'max-md:hidden': !selectedId }"
        :aria-label="t('runs.details')"
        :aria-busy="detailLoading"
      >
        <Button
          v-if="selectedId"
          :label="t('runs.backToList')"
          class="md:hidden mb-3"
          icon="i-lucide-arrow-left"
          variant="ghost"
          @click="selectedId = null"
        />
        <p v-if="detailLoading" role="status">{{ t('loading') }}</p>
        <div v-else-if="detailError" role="alert">
          <p class="text-n-ruby-11">{{ detailError }}</p>
          <Button
            :label="t('runs.retry')"
            variant="ghost"
            @click="select(selectedId)"
          />
        </div>
        <RunDetails
          v-else-if="detail && selectedId"
          :run="detail"
          :catalog="catalog"
        />
        <p v-else class="text-sm text-n-slate-11">{{ t('runs.select') }}</p>
      </section>
    </div>
    <footer
      v-if="observedAt"
      class="shrink-0 px-5 py-2 border-t border-n-weak text-xs text-n-slate-11"
    >
      {{ t('runs.updated', { at: dateLabel(observedAt) }) }}
    </footer>
  </section>
</template>
